import { z } from 'zod';

/**
 * Public POST /onboarding/signups — strict allowlist for `stepData` (fail-closed).
 * Unknown keys at any level are rejected so clients cannot smuggle PII/financial blobs.
 */
export const onboardingLeadStepDataSchema = z
  .object({
    personal: z
      .object({
        firstName: z.string().max(200).optional(),
        lastName: z.string().max(200).optional(),
        email: z.string().max(320).optional(),
        phone: z.string().max(64).optional(),
        jobTitle: z.string().max(200).optional(),
        companyName: z.string().max(200).optional(),
        dateOfBirth: z.string().max(32).optional(),
      })
      .strict()
      .optional(),
    entity: z
      .object({
        firstName: z.string().max(200).optional(),
        lastName: z.string().max(200).optional(),
        email: z.string().max(320).optional(),
        phone: z.string().max(64).optional(),
        investorType: z.string().max(80).optional(),
        entityName: z.string().max(200).optional(),
        title: z.string().max(120).optional(),
        address: z.string().max(500).optional(),
        city: z.string().max(120).optional(),
        state: z.string().max(8).optional(),
        zip: z.string().max(20).optional(),
      })
      .strict()
      .optional(),
    company: z
      .object({
        companyName: z.string().max(200).optional(),
        phone: z.string().max(64).optional(),
        address: z.string().max(500).optional(),
        city: z.string().max(120).optional(),
        state: z.string().max(8).optional(),
        zip: z.string().max(20).optional(),
        dba: z.string().max(200).optional(),
        teamSize: z.string().max(40).optional(),
        website: z.string().max(500).optional(),
      })
      .strict()
      .optional(),
    license: z
      .object({
        licenseNumber: z.string().max(120).optional(),
        licenseState: z.string().max(8).optional(),
        licenseExpiration: z.string().max(32).optional(),
        verifyLater: z.boolean().optional(),
      })
      .strict()
      .optional(),
    property: z
      .object({
        addPropertiesNow: z.enum(['yes', 'later', 'import']).optional(),
      })
      .strict()
      .optional(),
    'property-management-approach': z
      .object({
        managementStructure: z.enum(['self-manage-all', 'pm-for-all', 'mixed']).optional(),
        inviteCode: z.string().max(200).optional(),
        searchPM: z.string().max(200).optional(),
        linkMethod: z.string().max(80).optional(),
      })
      .strict()
      .optional(),
    'owner-profile': z
      .object({
        firstName: z.string().max(200).optional(),
        lastName: z.string().max(200).optional(),
        email: z.string().max(320).optional(),
        phone: z.string().max(64).optional(),
        ownershipType: z.string().max(40).optional(),
        entityName: z.string().max(200).optional(),
      })
      .strict()
      .optional(),
    'workflow-preferences': z
      .object({
        /** @deprecated Ignored — WO threshold is Property.repairApprovalThreshold only. */
        requireApprovalRepairs: z.union([z.string().max(32), z.number()]).optional(),
        monthlyReports: z.boolean().optional(),
        maintenanceAlerts: z.boolean().optional(),
      })
      .strict()
      .optional(),
    lease: z
      .object({
        applicationId: z.string().max(80).optional(),
        acknowledge: z.boolean().optional(),
      })
      .strict()
      .optional(),
    emergency: z
      .object({
        emergencyContactName: z.string().max(200).optional(),
        emergencyContactPhone: z.string().max(64).optional(),
        emergencyContactRelation: z.string().max(200).optional(),
      })
      .strict()
      .optional(),
    applicationId: z.string().max(80).optional(),
    business: z
      .object({
        businessName: z.string().max(300).optional(),
        contactFirstName: z.string().max(120).optional(),
        contactLastName: z.string().max(120).optional(),
        email: z.string().max(320).optional(),
        phone: z.string().max(64).optional(),
      })
      .strict()
      .optional(),
    terms: z
      .object({
        agreedToTerms: z.boolean().optional(),
        offeringCode: z.string().max(80).optional(),
        investmentAmount: z.number().optional(),
        investmentType: z.string().max(80).optional(),
      })
      .strict()
      .optional(),
    accreditation: z
      .object({
        status: z.string().max(40).optional(),
        accreditationType: z.string().max(80).optional(),
        notes: z.string().max(2000).optional(),
      })
      .strict()
      .optional(),
    services: z
      .object({
        serviceLevel: z.enum(['full-service', 'lite', 'both']).optional(),
      })
      .strict()
      .optional(),
    welcome: z.object({}).strict().optional(),
  })
  .strict();

export type OnboardingLeadStepData = z.infer<typeof onboardingLeadStepDataSchema>;
